import { devLog, devError } from '../utils/devLogger';
/**
 * Document Preview Component
 *
 * Provides in-app preview for documents without requiring download
 * Supports: PDF, Images (JPG, PNG, GIF), DOCX (via Google Docs Viewer)
 *
 * Non-breaking: Adds preview functionality while keeping download option
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Badge, Nav, Tab } from 'react-bootstrap';
import { getDownloadURL } from 'firebase/storage';
import '../styles/brand.css';

const DocumentPreview = ({
  show,
  onHide,
  documentUrl,
  documentName,
  documentType,
  storageRef,
  downloadName
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewMode, setViewMode] = useState('preview');

  // Determine file type from document name or type
  const getFileExtension = (filename) => {
    return filename?.split('.').pop()?.toLowerCase() || '';
  };

  const extension = documentType || getFileExtension(documentName);

  // Check if document is previewable
  const isPreviewable = () => {
    const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'];
    return previewableTypes.includes(extension);
  };

  const isImage = () => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
    return imageTypes.includes(extension);
  };

  const isPDF = () => {
    return extension === 'pdf';
  };

  const isOfficeDocument = () => {
    const officeTypes = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
    return officeTypes.includes(extension);
  };

  // Fetch document URL
  useEffect(() => {
    const fetchDocumentUrl = async () => {
      if (!show) return;

      setLoading(true);
      setError(null);

      try {
        let url = documentUrl;

        // If storageRef provided, get download URL
        if (storageRef && !documentUrl) {
          url = await getDownloadURL(storageRef);
        }

        if (!url) {
          throw new Error('No document URL provided');
        }

        setPreviewUrl(url);
        setLoading(false);
      } catch (err) {
        devError('Error loading document:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchDocumentUrl();
  }, [show, documentUrl, storageRef]);

  // Handle download
  const handleDownload = () => {
    if (previewUrl) {
      const link = document.createElement('a');
      link.href = previewUrl;
      link.download = downloadName || documentName || 'document';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Render preview based on file type
  const renderPreview = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger" className="m-3">
          <Alert.Heading>Preview Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <Button variant="outline-primary" onClick={handleDownload}>
            Download Document Instead
          </Button>
        </Alert>
      );
    }

    if (!previewUrl) {
      return (
        <Alert variant="warning" className="m-3">
          No preview available. Please download the document.
        </Alert>
      );
    }

    // Image preview
    if (isImage()) {
      return (
        <div className="text-center p-3" style={{
          maxHeight: '70vh',
          overflow: 'auto',
          backgroundColor: '#f8f9fa'
        }}>
          <img
            src={previewUrl}
            alt={documentName}
            style={{
              maxWidth: '100%',
              height: 'auto',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    }

    // PDF preview using browser native viewer
    if (isPDF()) {
      return (
        <div style={{ height: '70vh', width: '100%' }}>
          <iframe
            src={`${previewUrl}#view=FitH`}
            title={documentName}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            onError={() => setError('Failed to load PDF')}
          />
        </div>
      );
    }

    // Office documents via Google Docs Viewer
    if (isOfficeDocument()) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(previewUrl)}&embedded=true`;

      return (
        <>
          <Alert variant="info" className="m-3">
            <strong>Office Document Preview</strong>
            <p className="mb-0">
              This document is displayed using Google Docs Viewer.
              For full functionality, please download the document.
            </p>
          </Alert>
          <div style={{ height: '60vh', width: '100%' }}>
            <iframe
              src={viewerUrl}
              title={documentName}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              onError={() => setError('Failed to load document. Please try downloading.')}
            />
          </div>
        </>
      );
    }

    // Fallback for unsupported types
    return (
      <Alert variant="info" className="m-3">
        <Alert.Heading>Preview Not Available</Alert.Heading>
        <p>
          Preview is not supported for <Badge bg="secondary">.{extension}</Badge> files.
        </p>
        <hr />
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={handleDownload}>
            Download Document
          </Button>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
        </div>
      </Alert>
    );
  };

  // Get file type badge color
  const getFileTypeBadgeColor = () => {
    if (isPDF()) return 'danger';
    if (isImage()) return 'success';
    if (isOfficeDocument()) return 'primary';
    return 'secondary';
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center gap-2">
            <span>📄 Document Preview</span>
            <Badge bg={getFileTypeBadgeColor()}>
              .{extension.toUpperCase()}
            </Badge>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        {/* Tab Navigation for different views */}
        {isPreviewable() && !loading && !error && (
          <Tab.Container activeKey={viewMode} onSelect={(k) => setViewMode(k)}>
            <Nav variant="tabs" className="px-3 pt-2">
              <Nav.Item>
                <Nav.Link eventKey="preview">Preview</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="info">Info</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="preview" className="p-0">
                {renderPreview()}
              </Tab.Pane>

              <Tab.Pane eventKey="info" className="p-3">
                <h5>Document Information</h5>
                <table className="table">
                  <tbody>
                    <tr>
                      <th style={{ width: '200px' }}>File Name</th>
                      <td>{documentName || 'Unknown'}</td>
                    </tr>
                    <tr>
                      <th>File Type</th>
                      <td>
                        <Badge bg={getFileTypeBadgeColor()}>
                          .{extension.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                    <tr>
                      <th>Previewable</th>
                      <td>
                        {isPreviewable() ? (
                          <Badge bg="success">✓ Yes</Badge>
                        ) : (
                          <Badge bg="secondary">✗ No</Badge>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Download URL</th>
                      <td>
                        <code style={{
                          fontSize: '0.75rem',
                          wordBreak: 'break-all',
                          display: 'block',
                          maxHeight: '100px',
                          overflow: 'auto'
                        }}>
                          {previewUrl ? previewUrl.substring(0, 100) + '...' : 'Loading...'}
                        </code>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        )}

        {/* Show preview directly if not using tabs */}
        {!isPreviewable() || loading || error ? renderPreview() : null}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <div className="text-muted small">
            {documentName && (
              <span title={documentName}>
                {documentName.length > 50
                  ? documentName.substring(0, 47) + '...'
                  : documentName}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={handleDownload}
              disabled={!previewUrl}
            >
              📥 Download
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentPreview;
